import { Button, Editor, Input, Label, Modal } from "@mixitone/components";
import type { FundingOutreach } from "@mixitone/models";
import { ApplicationView } from "@mixitone/mvc";
import { centsToDollars, dollarsToCents, formatOccurredAt, FUNDING_TEMPLATE_VARIABLES } from "@mixitone/util";
import { CommunicationBody } from "components/Pages/Funding/CommunicationBody";
import { FundingOutreachFormController } from "components/Pages/Funding/FundingOutreachFormController";
import { sentimentLabel, sentimentStyles } from "lib/fundingSentiment";

/** Modal to view a single outreach communication (email or manual message) with prev/next, reply, and workflow actions. */
const FundingOutreachViewEmailModal = () => {
  const controller = FundingOutreachFormController.use();
  const {
    viewingCommunication,
    communications,
    analysesByCommId,
    record,
    replyingInViewModal,
    composeSubject,
    composeBody,
    composeMergeValues,
    sendingEmail,
  } = controller.state;
  const r = record as FundingOutreach | null;
  const status = r?.status ?? "to_contact";
  const potentialPounds = centsToDollars(r?.amount_potential_cents);
  const canSend = Boolean(r?.funder?.contact_email?.trim());
  const funder = r?.funder;
  const contactEmail = funder?.contact_email?.trim();

  if (!viewingCommunication) return null;

  const vc = viewingCommunication as typeof viewingCommunication & {
    channel?: "email" | "manual";
    opened_at?: string | Date | null;
    sent_by_account_user?: { name?: string | null } | null;
  };
  const isManual = vc.channel === "manual";
  const comms = Array.from(communications);
  const viewingIndex = comms.findIndex((c) => c.id === vc.id);
  const hasPrev = viewingIndex > 0;
  const hasNext = viewingIndex >= 0 && viewingIndex < comms.length - 1;
  const viewAnalysis = vc.direction === "inbound" && vc.id != null ? analysesByCommId?.[vc.id] : null;
  const viewSentiment = viewAnalysis?.sentiment ?? "unknown";
  const viewStyles = viewAnalysis ? sentimentStyles(viewSentiment) : null;

  const handleStartReply = () => {
    void controller.actionStartInlineReply(vc.subject ?? undefined);
  };

  const handleCancelReply = () => {
    controller.actionCancelInlineReply();
  };

  const handleSendInline = async () => {
    if (!composeSubject.trim() || !composeBody.trim()) return;
    const result = await controller.actionSendEmail(composeSubject.trim(), composeBody.trim());
    controller.actionCancelInlineReply();
    if (result?.pendingReview) controller.actionSetPendingReviewMessage(true);
  };

  return (
    <Modal
      title={vc.subject || (isManual ? "Message" : "Email")}
      className="flex h-[85vh] max-h-[85vh] w-full min-w-[85vw] max-w-2xl flex-col overflow-hidden sm:h-[85vh] sm:max-h-[85vh] sm:min-w-[85vw] sm:w-full"
      onClose={() => controller.actionSetViewingCommunication(null)}
      portal
    >
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden">
          <div className="shrink-0 space-y-2 text-sm">
            {isManual ? (
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                <span className="text-gray-500">Source:</span>
                <span>{vc.from_name ?? "Note"}</span>
              </div>
            ) : (
              <>
                <div className="flex flex-wrap gap-x-4 gap-y-1">
                  <span className="text-gray-500">From:</span>
                  <span>{vc.from_email ?? "—"}</span>
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1">
                  <span className="text-gray-500">To:</span>
                  <span>{vc.to_email ?? "—"}</span>
                </div>
              </>
            )}
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              <span className="text-gray-500">Date:</span>
              <span>{formatOccurredAt(vc.occurred_at)}</span>
            </div>
            {vc.direction === "outbound" && vc.sent_by_account_user?.name && (
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                <span className="text-gray-500">Sent by:</span>
                <span>{vc.sent_by_account_user.name}</span>
              </div>
            )}
            {vc.direction === "outbound" && (
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                <span className="text-gray-500">Opened:</span>
                <span>{vc.opened_at ? formatOccurredAt(vc.opened_at) : "Not opened yet"}</span>
              </div>
            )}
            {viewAnalysis && viewStyles && (
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span className="text-gray-500">Sentiment:</span>
                <span
                  className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-medium ${viewStyles.badge}`}
                >
                  {sentimentLabel(viewSentiment)}
                </span>
              </div>
            )}
          </div>
          <CommunicationBody
            bodyHtml={vc.body_html}
            bodyText={vc.body_text}
            className="min-h-0 flex-1 overflow-y-auto rounded border border-gray-200 bg-gray-50/50 p-4 text-sm text-gray-700 [&_a]:text-blue-600"
          />
          {vc.direction === "inbound" && !replyingInViewModal && (
            <div className="shrink-0 flex flex-wrap items-center gap-x-4 gap-y-2 rounded border border-gray-200 bg-gray-50/80 px-3 py-2 text-sm">
              <span className="text-gray-600">
                <span className="text-gray-500">Indicated amount:</span>{" "}
                <Input
                  type="number"
                  min={0}
                  step={0.01}
                  className="inline-block h-7 w-20 rounded border-gray-300 px-2 text-sm"
                  placeholder="—"
                  value={potentialPounds != null ? String(potentialPounds) : ""}
                  onChange={(e) => {
                    const val = e.target.value.trim();
                    const r2 = controller.state.record as FundingOutreach | null;
                    const cents = dollarsToCents(val);
                    const valid = val === "" || cents !== undefined;
                    if (r2 && valid) {
                      r2.amount_potential_cents = cents;
                    }
                  }}
                  onBlur={(e) => {
                    const val = e.target.value.trim();
                    const n = val === "" ? null : Number.parseFloat(val);
                    if (val === "" || (n != null && Number.isFinite(n) && n >= 0)) {
                      controller.actionUpdatePotentialAmount(val === "" ? null : n);
                    }
                  }}
                  title="Indicated amount (the assistant may have filled from this reply)"
                />
              </span>
              {status === "contacted" && (
                <Button
                  kind="secondary"
                  className="!py-1 !text-xs"
                  onClick={() => controller.actionSetStatusReplied()}
                  title="Mark that the funder has replied"
                >
                  Mark as replied
                </Button>
              )}
              {status === "replied" && (
                <>
                  <Button
                    kind="primary"
                    className="!py-1 !text-xs"
                    onClick={() => controller.actionOpenAcceptAmountModal()}
                    title="Record that funding was accepted"
                  >
                    Move to funding
                  </Button>
                  <Button
                    kind="secondary"
                    className="!py-1 !text-xs"
                    onClick={() => controller.actionSetStatusDeclined()}
                    title="Record that the funder declined"
                  >
                    Move to declining
                  </Button>
                </>
              )}
            </div>
          )}
        </div>

        {replyingInViewModal && canSend && contactEmail && (
          <div
            className="funding-inline-reply-dock-animate -mx-2 shrink-0 border-t-4 border-brand-600 bg-white px-4 pb-4 pt-4 shadow-[0_-12px_32px_-8px_rgba(0,0,0,0.12)] sm:-mx-6 sm:px-6"
            role="region"
            aria-label="Reply composer"
          >
            <h3 className="mb-3 text-sm font-semibold text-brand-800">Your reply</h3>
            <div className="space-y-3">
              <div>
                <Label>To</Label>
                <div className="rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                  {funder?.name ?? ""} &lt;{contactEmail}&gt;
                </div>
              </div>
              <div className="flex min-h-0 max-h-[min(40vh,280px)] flex-col">
                <Label className="shrink-0">Message</Label>
                <div className="min-h-0 flex-1 overflow-y-auto rounded border border-gray-200">
                  <Editor
                    autoFocus
                    value={composeBody}
                    onChange={(e) => controller.actionSetComposeBody(e.target.value)}
                    className="min-h-[160px] w-full border-0"
                    insertableVariables={FUNDING_TEMPLATE_VARIABLES.map((variable) => ({
                      label: `${variable.label} (${composeMergeValues[variable.key] ?? ""})`,
                      token: composeMergeValues[variable.key] ?? "",
                    }))}
                  />
                </div>
              </div>
            </div>
            <div className="mt-4 flex flex-wrap items-center justify-end gap-2 border-t border-gray-200 pt-3">
              <Button kind="secondary" onClick={handleCancelReply} disabled={sendingEmail}>
                Cancel
              </Button>
              <Button
                kind="primary"
                onClick={() => void handleSendInline()}
                disabled={sendingEmail || !composeSubject.trim() || !composeBody.trim()}
                spinner={sendingEmail}
              >
                Send email
              </Button>
            </div>
          </div>
        )}

        {!replyingInViewModal && (
          <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-t border-gray-200 bg-slate-50/80 pt-3">
            <div className="flex gap-2">
              <Button
                kind="secondary"
                onClick={() => controller.actionSetViewingCommunication(comms[viewingIndex - 1])}
                disabled={!hasPrev}
                title={hasPrev ? "Previous message" : undefined}
              >
                Previous
              </Button>
              <Button
                kind="secondary"
                onClick={() => controller.actionSetViewingCommunication(comms[viewingIndex + 1])}
                disabled={!hasNext}
                title={hasNext ? "Next message" : undefined}
              >
                Next
              </Button>
            </div>
            <div className="flex gap-2">
              {canSend && (
                <Button kind="primary" onClick={handleStartReply}>
                  Reply
                </Button>
              )}
              <Button kind="secondary" onClick={() => controller.actionSetViewingCommunication(null)}>
                Close
              </Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ApplicationView(FundingOutreachViewEmailModal);
